/**
 * @component FaqAdminPanel
 * @description FAQ 질문·답변을 추가·수정·삭제하고 전체를 저장하는 관리자 패널입니다.
 * @usage AdminPage의 'FAQ' 탭에서 렌더링됩니다.
 */

import { useEffect, useState } from 'react';
import AdminToolbar from '../../components/admin/AdminToolbar';
import AdminRowButton from '../../components/admin/AdminRowButton';
import { useEditableList } from '../../hooks/useEditableList';
import { useSeedReset } from '../../hooks/useSeedReset';
import ModalPopup from '../../components/common/ModalPopup';
import * as adminApi from '../../services/adminApi';
import { AdminAuthError } from '../../services/adminApi';

function makeEmptyRow() {
  return { question: '', answer: '' };
}

// [기능] 기존 편집과 초기화 결과 반영을 제공하는 관리자 패널입니다.
export default function FaqAdminPanel({ onUnauthorized }) {
  // [비즈니스] 새로 등록한 FAQ가 공개 페이지 목록 맨 위에 노출되도록 새 행을 위쪽에 추가합니다.
  const { rows, setRowsFromServer, updateRow, toggleDelete, addRow, removeRow, resetDraft, buildPayload, isDirty } =
    useEditableList(makeEmptyRow, { addTo: 'top' });

  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    let isActive = true;
    adminApi
      .fetchFaq()
      .then((items) => {
        if (isActive) setRowsFromServer(items);
      })
      .catch((error) => {
        if (isActive) setErrorMessage(error.message);
      })
      .finally(() => {
        if (isActive) setLoading(false);
      });
    return () => {
      isActive = false;
    };
  }, [setRowsFromServer]);

  // [기능] 삭제 표시를 제외한 모든 항목을 저장합니다.
  const handleSave = async () => {
    setErrorMessage('');
    setIsSaving(true);
    try {
      // [비즈니스] 질문이 비어 있는 항목은 목록에서 의미가 없으므로 저장 대상에서 제외합니다.
      const payload = buildPayload((row) => {
        const question = String(row.question).trim();
        if (!question) return null;
        return { question, answer: String(row.answer).trim() };
      });

      const saved = await adminApi.saveFaq(payload);
      setRowsFromServer(saved);
    } catch (error) {
      if (error instanceof AdminAuthError) {
        onUnauthorized();
        return;
      }
      setErrorMessage(error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const { isResetting, requestReset, dialogProps } = useSeedReset({
    target: 'faq',
    isDirty: isDirty,
    // [기능] 초기화 응답으로 편집 상태를 교체하고 기존 오류를 지웁니다.
    onApplied: (data) => {
      setErrorMessage('');
      setRowsFromServer(data);
    },
    onError: setErrorMessage,
    onUnauthorized,
  });

  if (loading) return <p className="admin-loading">불러오는 중...</p>;

  return (
    <section className="admin-panel">
      <AdminToolbar
        title="FAQ 관리"
        hint="질문과 답변을 직접 수정할 수 있습니다. 표시 순서는 목록 순서를 따릅니다."
        errorMessage={errorMessage}
        isSaving={isSaving}
        isDirty={isDirty}
        onSave={handleSave}
        onReset={resetDraft}
        onSeedReset={requestReset}
        isResetting={isResetting}
      />

      {/* [비즈니스] 새 항목이 목록 맨 위에 추가되므로 버튼도 목록 위에 둡니다. */}
      <button type="button" className="admin-btn admin-btn-ghost admin-add-btn admin-add-btn-top" onClick={addRow}>
        + FAQ 추가
      </button>

      <ul className="admin-rows">
        {rows.map((row, index) => (
          <li key={row._uid} className={`admin-row admin-row-faq${row._deleted ? ' is-deleted' : ''}`}>
            <div className="admin-row-faq-head">
              <span className="admin-row-index">{index + 1}</span>
              <input
                type="text"
                className="admin-input"
                value={row.question}
                onChange={(e) => updateRow(row._uid, 'question', e.target.value)}
                placeholder="질문"
                disabled={row._deleted}
                aria-label="질문"
              />
              {row._isNew ? (
                <AdminRowButton variant="delete" title="행 제거" onClick={() => removeRow(row._uid)} />
              ) : (
                <AdminRowButton
                  variant={row._deleted ? 'restore' : 'delete'}
                  onClick={() => toggleDelete(row._uid)}
                />
              )}
            </div>
            <textarea
              className="admin-input admin-textarea"
              value={row.answer}
              onChange={(e) => updateRow(row._uid, 'answer', e.target.value)}
              placeholder="답변"
              disabled={row._deleted}
              aria-label="답변"
              rows={3}
            />
          </li>
        ))}
      </ul>

      <ModalPopup {...dialogProps} />
    </section>
  );
}
